import type { WorkflowConfig, WorkflowTransition } from '@gzl10/nexus-sdk'

/**
 * Validate that a state transition is allowed by the workflow config.
 * Returns the matched transition (for CASL check), or undefined if no change.
 * Throws if the transition is invalid.
 */
export function validateWorkflowTransition(
  workflow: WorkflowConfig,
  currentValue: string,
  newValue: string | undefined
): WorkflowTransition | undefined {
  if (newValue === undefined || newValue === currentValue) return undefined

  const stateValues = new Set(workflow.states.map(s => s.value))

  if (!stateValues.has(currentValue)) {
    throw new Error(`Invalid workflow state: '${currentValue}' is not defined in workflow`)
  }
  if (!stateValues.has(newValue)) {
    throw new Error(`Invalid workflow state: '${newValue}' is not defined in workflow`)
  }

  const transition = workflow.transitions.find(t => t.from === currentValue && t.to === newValue)
  if (!transition) {
    throw new Error(`Invalid workflow transition from '${currentValue}' to '${newValue}'`)
  }

  return transition
}

/**
 * Get all valid transitions from the current state.
 */
export function getValidTransitions(workflow: WorkflowConfig, currentValue: string): WorkflowTransition[] {
  return workflow.transitions.filter(t => t.from === currentValue)
}
